import { ogImageMeta } from "@/lib/og-images";
import { createFileRoute, Link } from "@tanstack/react-router";
import { TalkToJonButton } from "@/components/talk-to-jon";
import { RelatedGuides } from "@/components/related-guides";
import { SiteFooter } from "@/components/site-footer";

export const FAQS = [
  {
    q: "How do I export my database from Lovable Cloud?",
    a: "Open your project, go to the Cloud tab, then Overview, then Advanced settings. Under 'Export project data' click Export data, then Export on the Database card, then Start export. Lovable emails you when the dump is ready and you download it from the Cloud tab under Storage.",
  },
  {
    q: "How big can the export be, and how often can I run one?",
    a: "The database export is capped at 5 GB and you can request one every 24 hours. If you're near the cap, plan the migration around that window rather than discovering it on cutover day.",
  },
  {
    q: "Does the export include my storage files?",
    a: "No. The dump is schema and data only. Files in your storage buckets have to be downloaded separately from the Cloud tab under Storage, and edge function code and project secrets aren't included either.",
  },
  {
    q: "Will my users' passwords come across?",
    a: "Not in a usable form. Auth users export, but their password hashes don't come with them, so every migrated user needs to go through a password reset on first login. Build that flow before cutover, not after.",
  },
  {
    q: "Can I remove Lovable Cloud after exporting?",
    a: "You can, but it's permanent — the Cloud instance is deleted and there is no undo. Export, restore into your own Supabase, verify every flow against the new backend, and only then remove.",
  },
  {
    q: "Can you do the whole migration for us?",
    a: "Yes. That's the Lovable Cloud migration service — schema, data, auth users, storage, edge functions, RLS policies and the password reset flow, shaped around your project.",
  },
];

const TITLE = "How to export your data from Lovable Cloud";
const URL = "https://vibing.inc/guides/export-data-from-lovable-cloud";
const DESCRIPTION =
  "A step-by-step guide to exporting your data from Lovable Cloud: the exact click path, the 5 GB and 24-hour limits, what the dump leaves out, and how to restore into your own Supabase.";

export const Route = createFileRoute("/guides/export-data-from-lovable-cloud")({
  head: () => ({
    meta: [
      { title: "Export Your Data From Lovable Cloud (2026 Guide)" },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      ...ogImageMeta("guides-export-data", "Share card: export your data from Lovable Cloud"),
      {
        property: "og:description",
        content:
          "The exact click path for a Lovable Cloud database export, the limits nobody reads, what the dump leaves out, and a restore checklist for your own Supabase.",
      },
      { property: "og:url", content: URL },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: TITLE,
          description: DESCRIPTION,
          author: { "@type": "Organization", name: "the Vibing Company" },
          publisher: { "@type": "Organization", name: "the Vibing Company" },
          datePublished: "2026-08-04",
          mainEntityOfPage: URL,
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: Guide,
});

function Guide() {
  return (
    <div className="min-h-dvh bg-background text-foreground">
      <main id="main" tabIndex={-1}>
      <Hero />
      <Steps />
      <Body />
      <FAQ />
      <CTA />
      <RelatedGuides current="/guides/export-data-from-lovable-cloud" service={{ to: "/services/lovable-cloud-migration", label: "Lovable Cloud migration" }} />
      </main>
      <SiteFooter />
    </div>
  );
}


function Hero() {
  return (
    <section aria-labelledby="g-h" className="relative mx-auto max-w-[1100px] px-6 md:px-10 pt-[clamp(9rem,18vh,13rem)] pb-12">
      <h1
        id="g-h"
        className="mt-8 font-display tracking-[-0.035em] leading-[1.02]"
        style={{ fontSize: "clamp(2rem, 4.4vw, 3.5rem)" }}
      >
        How to export your data from Lovable Cloud
      </h1>
      <p className="mt-10 max-w-[62ch] text-xl md:text-2xl leading-snug text-foreground">
        Your rows are yours and getting them out takes about four clicks. What takes longer is knowing what the export leaves behind — storage files, function code, secrets, and every password your users have. Here's the whole picture.
      </p>
    </section>
  );
}

const STEPS = [
  { k: "01", v: "Open the project and go to the Cloud tab in the toolbar." },
  { k: "02", v: "Overview → Advanced settings → the 'Export project data' section → Export data." },
  { k: "03", v: "On the Database card, click Export, then confirm with Start export." },
  { k: "04", v: "Wait for the email. When it lands, download the dump from the Cloud tab under Storage." },
];

function Steps() {
  return (
    <section aria-labelledby="s-h" className="relative border-t border-border/60 bg-secondary/40">
      <div className="mx-auto max-w-[1100px] px-6 md:px-10 py-20 md:py-28 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <h2 id="s-h" className="fluid-h2 font-display max-w-[14ch]">
            The four clicks.
          </h2>
          <p className="mt-6 text-muted-foreground max-w-prose">
            The export is a schema-plus-data dump of your database. It's generated in the background, so nothing appears instantly.
          </p>
        </div>
        <ul className="md:col-span-7 md:col-start-6 border-t border-foreground/20">
          {STEPS.map((s) => (
            <li
              key={s.k}
              className="grid grid-cols-[60px_1fr] md:grid-cols-[90px_1fr] gap-6 items-baseline py-6 border-b border-foreground/20"
            >
              <span className="font-display text-electric tracking-[-0.03em]" style={{ fontSize: "clamp(1.5rem, 2.2vw, 2.25rem)" }}>
                {s.k}
              </span>
              <span className="text-base md:text-lg leading-snug">{s.v}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

const SECTIONS: { h: string; p: string[]; code?: string }[] = [
  {
    h: "Two different things people call 'export'",
    p: [
      "The first is a backup. You want a copy of your database sitting on your own disk, because a product with real customers in it shouldn't exist only inside one platform. That's a five-minute job and you should do it on a schedule.",
      "The second is leaving. You want your app running against your own Supabase project, with your own dashboard, your own keys and your own backups. The export is step one of that, but it's maybe a third of the work.",
      "Both start in the same place, so run the export either way. What differs is everything you do afterwards.",
    ],
  },
  {
    h: "The limits nobody reads until they hit them",
    p: [
      "A database export is capped at 5 GB, and you can request one every 24 hours. Neither number matters on a young project and both matter enormously on cutover day, when you assumed you could re-run the dump twice in an afternoon.",
      "So take a practice export a week before you actually need one. You'll find out how big your data really is, how long the email takes, and whether the dump restores cleanly — while you still have room to fix things.",
    ],
  },
  {
    h: "What the export does not include",
    p: [
      "Storage bucket files. The dump is schema and data only; anything your users uploaded is downloaded separately from the Cloud tab under Storage. On a media-heavy app this is the bulk of what you actually care about.",
      "Edge function code. That lives in your project's repository, not in the database, so it comes across with your codebase rather than the dump.",
      "Project secrets. API keys, signing secrets, webhook secrets — none of them are exported, by design. Write down which secrets exist before you migrate, because rediscovering them from broken runtime errors afterwards is miserable.",
      "Row-level security policies come across with the schema, but verify them on the other side rather than assuming. A policy that silently didn't apply is the most expensive kind of migration bug.",
    ],
  },
  {
    h: "The passwords gotcha",
    p: [
      "This is the one that catches everybody. Your auth users export, but their passwords don't come across in a usable form. Which means the morning after you migrate, every single existing user cannot log in.",
      "The fix is a password reset flow, built and tested before you cut over, plus an email to your users telling them what's happening so a reset link doesn't read like a phishing attempt. If you use magic links or social login, you're mostly spared — those users just sign in again.",
      "Plan the migration for a quiet window and be ready to answer support the same day. It's a one-time cost, but it's a real one.",
    ],
  },
  {
    h: "Restoring into your own Supabase",
    p: [
      "Order matters. Create the project, load the schema, then the data, then re-create anything the dump didn't carry: RLS policies verified one table at a time, storage buckets and their files re-uploaded, edge functions deployed, secrets re-entered.",
      "Then flip the environment variables in your app. In a Lovable project the code keeps pointing at Supabase either way — only the URL and the keys change, which is why the app itself usually needs no rewriting.",
      "Then smoke-test everything with real intent: sign up, log in, reset a password, upload a file, run a payment, trigger a webhook. Not the happy path you remember, the whole surface. Keep the old Cloud instance alive until you've done it.",
    ],
  },
  {
    h: "Removing Cloud is permanent",
    p: [
      "When you remove Lovable Cloud from a project, the instance is deleted and it cannot be undone. There's no grace period and no recovery.",
      "So the sequence is: export, restore, verify against the new backend for a few days with real traffic, and only then remove. The overlap costs you almost nothing and buys you the ability to change your mind.",
    ],
  },
];

function Body() {
  return (
    <article className="mx-auto max-w-[1100px] px-6 md:px-10 py-12 md:py-20 space-y-16 md:space-y-24">
      {SECTIONS.map((s) => (
        <section key={s.h} className="grid md:grid-cols-12 gap-8 md:gap-10 border-t border-border/60 pt-12 md:pt-16">
          <div className="md:col-span-4">
            <h2
              className="mt-4 font-display tracking-[-0.03em] leading-[1.02]"
              style={{ fontSize: "clamp(1.75rem, 3vw, 2.5rem)" }}
            >
              {s.h}
            </h2>
          </div>
          <div className="md:col-span-8 space-y-5 text-base md:text-lg leading-relaxed text-foreground/90 max-w-[64ch]">
            {s.p.map((para, j) => (
              <p key={j}>{para}</p>
            ))}
            {s.code ? (
              <pre tabIndex={0} className="mt-2 overflow-x-auto rounded-2xl border border-border bg-secondary/50 p-5 text-[13px] leading-relaxed">
                <code>{s.code}</code>
              </pre>
            ) : null}
          </div>
        </section>
      ))}
    </article>
  );
}

function FAQ() {
  return (
    <section
      aria-labelledby="faq-h"
      className="relative border-t border-border/60 mx-auto max-w-[1100px] px-6 md:px-10 py-20 md:py-32"
    >
      <h2 id="faq-h" className="fluid-h2 font-display max-w-[20ch]">
        Lovable Cloud exports, answered.
      </h2>
      <dl className="mt-14 border-t border-border">
        {FAQS.map((f) => (
          <div key={f.q} className="grid md:grid-cols-12 gap-6 py-8 border-b border-border">
            <dt className="md:col-span-5 font-display text-xl md:text-2xl tracking-[-0.02em] leading-snug">{f.q}</dt>
            <dd className="md:col-span-6 md:col-start-7 text-base md:text-lg text-muted-foreground leading-relaxed">
              {f.a}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function CTA() {
  return (
    <section aria-labelledby="cta-h" className="relative border-t border-border/60 bg-foreground text-background">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-24 md:py-40 grid md:grid-cols-12 gap-10 items-end">
        <div className="md:col-span-7">
          <h2
            id="cta-h"
            className="font-display tracking-[-0.035em] leading-[1.02]"
            style={{ fontSize: "clamp(2rem, 4.4vw, 3.5rem)" }}
          >
            Or let us move it.
          </h2>
          <p className="mt-8 text-lg md:text-xl text-background/70 max-w-[52ch] leading-snug">
            Schema, data, auth users, storage, edge functions, RLS and the password reset flow — moved carefully into your own Supabase.
          </p>
          <div className="mt-10">
            <TalkToJonButton variant="outline-light" />
          </div>
        </div>
        <div className="md:col-span-4 md:col-start-9 space-y-4">
          <Link
            to="/services/lovable-cloud-migration"
            className="block font-display text-electric-bright"
            style={{ fontSize: "clamp(1.25rem, 1.8vw, 1.5rem)" }}
          >
            → Lovable Cloud migration
          </Link>
          <Link
            to="/guides/what-is-vibe-coding"
            className="block font-display text-electric-bright"
            style={{ fontSize: "clamp(1.25rem, 1.8vw, 1.5rem)" }}
          >
            → What is vibe coding?
          </Link>
        </div>
      </div>
    </section>
  );
}
